import { useState, useEffect } from 'react';

/**
 * Hook to detect media queries
 * Optimized to prevent forced reflows by using lazy initialization
 * Used for mobile/desktop detection and motion optimization
 */
export const useMediaQuery = (query: string): boolean => {
  // Use lazy initialization to get initial value without causing reflow in useEffect
  const [matches, setMatches] = useState(() => {
    // Check if window is available (SSR safety)
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    // Guard for SSR
    if (typeof window === 'undefined') return;

    const media = window.matchMedia(query);
    
    // Batch updates using RAF to prevent forced reflows
    let rafId: number;
    const updateMatches = () => {
      if (media.matches !== matches) {
        setMatches(media.matches);
      }
    };

    // Create listener with RAF batching
    const listener = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateMatches);
    };
    
    // Initial check
    updateMatches();
    
    // Use modern API with fallback
    media.addEventListener('change', listener);
    return () => {
      media.removeEventListener('change', listener);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [query, matches]);

  return matches;
};

// Preset hooks
export const useIsMobile = () => useMediaQuery('(max-width: 768px)');
export const useIsTablet = () => useMediaQuery('(min-width: 769px) and (max-width: 1024px)');
export const useIsDesktop = () => useMediaQuery('(min-width: 1025px)');
export const usePrefersReducedMotion = () => useMediaQuery('(prefers-reduced-motion: reduce)');
